import {PolymerElement, html} from '@polymer/polymer';
import {afterNextRender} from '@polymer/polymer/lib/utils/render-status.js';
import {ScopeBinding} from '../../behaviors/keli-binding-behavior.js';
import {InputBehavior} from '../../behaviors/input-behavior.js';
import {DropdownBehavior} from '../../behaviors/dropdown-behavior.js';
import {InputSharedStylesHTML} from '../../shared-styles/input.js';
import {HotkeyBehavior} from '../../behaviors/hotkey-behavior.js';

class KeliLookup extends ScopeBinding(DropdownBehavior(InputBehavior(HotkeyBehavior(PolymerElement)))) {
  static get template() {
    return html`
      ${InputSharedStylesHTML}

      <keli-lookup-controller id="controller"></keli-lookup-controller>
      <keli-input-container id="container"
                            label="[[caption]]"
                            input-has-content="[[computeInputHasContent(active, hasValue)]]"
                            icon="[[lookupIcon]]"
                            always-float-label="[[masked]]"
                            focused="[[focused]]"
                            disabled="[[disabled]]"
                            invalid="[[invalid]]"
                            error-message="[[errorMessage]]"
                            hotkey="[[formattedShortcutKey]]"
                            max-length="[[maxLength]]"
                            current-length="[[currentValueLength]]"
                            animating="[[loadingItems]]"
                            chrome="[[chrome]]"
                            help-text="[[helpText]]">

        <input type="text" id="input" value="{{currentValue::input}}" slot="input" placeholder$="[[placeholderText]]">
      </keli-input-container>`;
  }

  static get is() {
    return 'keli-lookup';
  }

  static get properties() {
    return {
      delay: {type: Number, scopevar: true},
      elementInitialized: {type: Boolean},
      filterMode: {type: String, scopevar: true},
      forceSelect: {type: Boolean, scopevar: true},
      hasValue: {type: Boolean},
      insertable: {type: Boolean},
      keliControlTypes: {type: Array, value: ['lookup', 'join']},
      keliScopeVarOptions: {type: Object, value: {confine: true}},
      lastSelectedValue: {type: String},
      loadingItems: {type: Boolean, observer: 'loadingItemsChanged'},
      lookupIcon: {type: String, computed: 'computeLookupIcon(icon, stringMatching, elementInitialized)'},
      maxChoices: {type: Number, scopevar: true},
      minimumToSearch: {type: Number, scopevar: true},
      placeholderText: {type: String, computed: 'computePlaceholderText(hasValue, displayValue, elementInitialized)'},
      selectedItem: {type: Object, observer: 'selectedItemChanged'},
      selectFirst: {type: Boolean, scopevar: true},
      showOverflowTooltip: {type: Boolean, value: true},
      stringMatching: {type: Boolean},
    }
  }

  get dropdownBehaviorStyles() {
    return `
keli-lookup-item.action {
  font-size: .8em;
  border-top: 1px solid var(--keli-subtle-border-color);
}
`;
  }

  constructor() {
    super();
    this.addEmitToKeli(['lookupItemAction']);
  }

  connectedCallback() {
    super.connectedCallback();

    this.elementInitialized = true;
    this.dropdownElement.sizeToElement = true;
    this.dropdownElement.positionElement = this.$.container;

    this.addEventListener('lookupChooseItem', this.handleChooseItem);
    this.addEventListener('lookupItemsLoaded', this.handleLookupItemsLoaded);
    this.addEventListener('keli-input-icon-activated', this.toggleList);

    this.AdjacentElement = new AdjacentElement({
      'this': this.dropdownElement,
      'selectorNode': this.dropdownElement.shadowRoot,
      'selector': 'keli-lookup-item',
      'wrap': false,
      'defaultCallback': (newElement) => {
        this.selectedItem = newElement;
      }
    });

    this.addHotkeyListener('raw-listener', this, {forceDefaults: true});
    this.setHotkeyListenerState('raw-listener', false);
    this.registerHotkey(KeliInterface.Hotkeys.lookup.choose, {
      down: event => {
        if (this.selectedItem) {
          this.selectedItem.activate(event);
        }
      }
    }, 'raw-listener');
    this.registerHotkey(KeliInterface.Hotkeys.lookup.cancel, {
      down: event => {
        if (this.dropdownElement.visible()) {
          event.preventDefault();
          event.stopPropagation();
          if (this.forceSelect !== false) {
            this.revertChanges();
          }
          this.selectedItem = null;
          this.dropdownElement.hide();
        }
      }
    }, 'raw-listener');
    this.registerHotkey(KeliInterface.Hotkeys.lookup.next, {
      down: event => {
        if (this.dropdownElement.visible()) {
          event.preventDefault();
          event.stopPropagation();
        }
        this.AdjacentElement.next(this.selectedItem);
      }
    }, 'raw-listener');
    this.registerHotkey(KeliInterface.Hotkeys.lookup.previous, {
      down: event => {
        if (this.dropdownElement.visible()) {
          event.preventDefault();
          event.stopPropagation();
        }
        this.AdjacentElement.previous(this.selectedItem);
      }
    }, 'raw-listener');
    this.registerHotkey(KeliInterface.Hotkeys.lookup.more, {
      down: event => {
        if (this.stringMatching) {
          this.toggleList(event);
        } else if (this.$.controller) {
          let filterValue;
          if (this.dropdownElement.visible()) {
            filterValue = this.currentValue;
            this.revertChanges();
            this.dropdownElement.hide();
          }
          this.lookupItemAction('list', filterValue);
        }
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.lookup.edit, {
      down: () => {
        if (!this.stringMatching && this.$.controller) {
          this.revertChanges();
          this.lookupItemAction('edit', this.currentValue);
        }
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.lookup.insert, {
      down: event => {
        if (!this.stringMatching && this.dropdownElement.visible() && this.$.controller) {
          event.preventDefault();
          event.stopPropagation();
          this.lookupItemAction('insert', this.currentValue);
          this.revertChanges();
        }
      }
    }, 'raw-listener');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('lookupChooseItem', this.handleChooseItem);
    this.removeEventListener('lookupItemsLoaded', this.handleLookupItemsLoaded);
    this.removeEventListener('keli-input-icon-activated', this.toggleList);

    this.selectedItem = null;

    if (this.AdjacentElement) {
      this.AdjacentElement.destroy();
      this.AdjacentElement = null;
    }
  }

  async keliReady(scope) {
    super.keliReady && await super.keliReady(scope);
    await this.$.controller.elementReady(this);
  }

  displayValueChanged(newValue) {
    this.lastSelectedValue = newValue;
  }

  dropdownElementVisibleChanged(newValue) {
    this.resetHotkeyState();
    this.setHotkeyListenerState('raw-listener', newValue);
    this.showOverflowTooltip = !newValue;

    if (newValue) {
      this.focusFirstItem();
    } else {
      if (this.forceSelect === false && !this.focused) {
        // Save when the dropdown element is hidden and forceSelect is false
        this._keliAccept();
      }
      if (!this.loadingItems && this.forceSelect !== false) {
        this.revertChanges();
      }
      this.clearLookupItems();
    }
  }

  computeInputHasContent(active, hasValue) {
    return active || hasValue;
  }

  computeLookupIcon(icon, stringMatching) {
    if (icon) {
      return icon;
    }
    return (stringMatching ? 'menu-down' : 'open-in-app');
  }

  loadingItemsChanged(newValue) {
    if (newValue) {
      this.showOverflowTooltip = false;
    }
  }

  computePlaceholderText(hasValue, displayValue) {
    if (hasValue && !displayValue) {
      return '(blank value)';
    }
    return '';
  }

  selectedItemChanged(newValue, oldValue) {
    if (oldValue) {
      oldValue.classList.remove('selected');
    }
    if (newValue) {
      newValue.classList.add('selected');
    }
    if (this.AdjacentElement) {
      this.AdjacentElement.element = newValue;
    }
  }

  handleBlur() {
    // Don't prevent default behavior if we've cleared the field.
    if (!this.currentValue) {
      return;
    }

    const requireSelection = (this.forceSelect !== false);
    if (!requireSelection && this.dropdownElement.visible()) {
      // Saving will be handled by the dropdown element hiding if it's open.
      return false;
    }
    if (requireSelection && this.loadingItems) {
      return false;
    }
    if (requireSelection && this.currentValue) {
      return false;
    }
  }

  handleChooseItem(event) {
    if (this.loadingItems) {
      return;
    }

    this.selectedItem = event.detail.element;

    if (event.detail.element.chooseAction) {
      let filterValue = this.currentValue;
      this.revertChanges();
      this.dropdownElement.hide();
      this.lookupItemAction(event.detail.element.chooseAction, filterValue);
      return;
    }

    this.currentValue = event.detail.element.caption;
    this.lastSelectedValue = this.currentValue;

    this.fireKeliAccepted = true;
    this._keliAccept(event);

    this.selectedItem = null;
    this.dropdownElement.hide();

    if (event?.detail?.focusInput) {
      afterNextRender(this, () => {
        this.dispatchEvent(new CustomEvent('focusElement', {detail: {element: this}}));
      });
    }
  }

  handleFocus() {
    this.resetHotkeyState();
  }

  handleInput(event) {
    const target = getEventTarget(event);
    if (target && this.focused) {
      this.clearLookupItems();
      if (this.$.controller) {
        this.$.controller.queryValue = target.value;
      }
      if (!target.value) {
        this.selectedItem = null;
        this.lastSelectedValue = null;
        this.dropdownElement.hide();
      }
    }
  }

  handleLookupItemsLoaded() {
    this.focusFirstItem();

    if (this.focused) {
      // Do not show the dropdown element if there are no items to show.
      if (this.currentValue && (this.forceSelect !== false || this.dropdownElement.queryContent('keli-lookup-item').length > 0)) {
        this.dropdownElement.show();
      }
    } else {
      // Choose the first item on the list if the user is no longer focused on the field.
      if (this.selectedItem && !this.selectedItem.chooseAction) {
        this.chooseSelectedItem();
      } else if (this.forceSelect !== false) {
        this.revertChanges();
      }
    }
  }

  toggleList(event) {
    if (this.disabled || !this.stringMatching) {
      return;
    }

    // Manually focus when the list is toggled to handle the icon being clicked.
    this._handleFocus(event);

    if (this.dropdownElement.visible()) {
      this.selectedItem = null;
      this.dropdownElement.hide();
    } else {
      this.dropdownElement.show();
      if (this.$.controller) {
        this.$.controller.queryValue = '';
        this.$.controller.executeSearch();
      }
    }
  }

  focusFirstItem() {
    if (this.selectFirst !== false) {
      this.AdjacentElement && this.AdjacentElement.first();
    } else {
      this.selectedItem = null;
    }
  }

  chooseSelectedItem() {
    if (!this.currentValue || !this.selectedItem) {
      return;
    }

    // Do not select items with special actions automatically.
    if (this.selectedItem.chooseAction) {
      return;
    }

    this.choosingSelectedItem = true; // Prevent recursion
    this.selectedItem.activate();
    this.dropdownElement.hide();
  }

  revertChanges() {
    this.fireKeliAccepted = false;
    this.selectedItem = null;
    this.currentValue = this.lastSelectedValue;
    if (this.$.controller) {
      this.$.controller.queryValue = '';
    }
  }

  clearLookupItems() {
    this.dropdownElement.removeContent(`keli-lookup-item`);
    this.selectedItem = null;
  }

  lookupItemAction(actionName, queryValue) {
    afterNextRender(this, () => {
      this.dispatchEvent(new CustomEvent('lookupItemAction', {
        detail: {
          action: actionName,
          input: queryValue,
          parent: this.$.controller && this.$.controller.keliname,
          valueId: this.value
        }
      }));
    });
  }

  createLookupItems(itemList) {
    if (!Array.isArray(itemList)) {
      itemList = [itemList];
    }
    for (let item of itemList) {
      let element = document.createElement('keli-lookup-item');
      element.lookup = this;
      element.caption = item.caption;
      if (item.rowid) {
        element.rowId = item.rowid;
      }
      if (item.hint) {
        element.hint = item.hint;
      }
      if (item.icon) {
        element.icon = item.icon;
      }
      if (item.action) {
        element.chooseAction = item.action;
      }
      this.dropdownElement.addContentElement(element);
    }

    if (!this.stringMatching) {
      let list = document.createElement('keli-lookup-item');
      list.classList.add('action');
      list.lookup = this;
      list.chooseAction = 'list';
      list.caption = 'Show as list';
      list.icon = 'view-list';
      list.hotkey = KeliInterface.Hotkeys.lookup.more.keys;
      this.dropdownElement.addContentElement(list);

      if (this.insertable) {
        let insert = document.createElement('keli-lookup-item');
        insert.classList.add('action');
        insert.lookup = this;
        insert.chooseAction = 'insert';
        insert.caption = 'Insert a new record';
        insert.icon = 'plus';
        insert.hotkey = KeliInterface.Hotkeys.lookup.insert[0].keys;
        this.dropdownElement.addContentElement(insert);
      }
    }

    // Close the dropdown when forceSelect is false, and there are no items, so escape works as expected.
    if (this.forceSelect === false && this.dropdownElement.queryContent('keli-lookup-item').length === 0) {
      this.dropdownElement.hide();
      return;
    }

    afterNextRender(this.dropdownElement, () => {
      this.dropdownElement.position();
    });
  }

  keliAccept(event) {
    // If we are waiting for items to be loaded, don't fire the accepted event yet.
    if (this.fireKeliAccepted && this.loadingItems && (!this.stringMatching || this.forceSelect)) {
      return false;
    }

    if (!this.choosingSelectedItem && this.fireKeliAccepted && this.dropdownElement?.visible() && this.forceSelect !== false) {
      this.chooseSelectedItem();
      return false;
    }
    this.choosingSelectedItem = false; // Prevent recursion

    if (this.fireKeliAccepted) {
      const selectedItem = event?.detail?.element || this.selectedItem;
      const rowId = selectedItem?.rowId;

      this._keliAcceptEventOverrides = {
        prev: this.displayValue,
        valueid: rowId
      };

      if (!this.stringMatching) {
        Object.assign(this._keliAcceptEventOverrides, {
          previd: this.$.controller.value
        });
        this.$.controller.value = rowId;
      }

      this.loadingItems = false;
    }

    this.$.controller.queryValue = undefined;
  }
}

customElements.define(KeliLookup.is, KeliLookup);
export {KeliLookup};
